import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent } from '@core/components/Dialog';
import { TextInput } from '@core/components/Inputs';
import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import { permissaoUtils } from '@core/util/permissaoUtil';
import { Grid, makeStyles } from '@material-ui/core';
import { SelectCliente } from 'app/components/Select';
import { cancelarAlert, excluirAlert } from 'app/services/alerts';
import { grupoClienteApi } from 'app/services/api/grupoClienteApi';
import * as Actions from 'app/store/actions';
import { Button } from 'devextreme-react';
import { useFormik } from 'formik';
import React, { useContext } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { GrupoClienteContext } from '../../GrupoClienteContext';
import CadastroGrupoClienteTable from './CadastroGrupoClienteTable';

const useStyles = makeStyles(() => ({
  btnAdicionar: {
    height: '50%',
    width: '70%',
    fontSize: '14px',
    color: '#53c4e6',
    marginTop: '7%',
    border: '1px solid #53c4e6',
    borderRadius: '5px',
    textAlign: 'center',
  },
}));

const CadastroGrupoCliente = ({ fetchGruposCliente }) => {
  const classes = useStyles();

  const [, setFormulario] = useContext(GrupoClienteContext).formulario;
  const [loading, setLoading] = useContext(GrupoClienteContext).loading;
  const [grupoCliente, setGrupoCliente] = useContext(GrupoClienteContext).grupoCliente;
  const [page] = useContext(GrupoClienteContext).page;
  const [size] = useContext(GrupoClienteContext).size;
  const [sort] = useContext(GrupoClienteContext).sort;

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  useReduxLoading(loading);

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  const grupoClienteSchema = Yup.object().shape({
    nome: Yup.string()
      .required(mensagemCampoObrigatorio('o nome do grupo.'))
      .min(3, 'É necessário informar pelo menos 3 caracteres no campo.'),
    listaClientes: Yup.array().required('Selecione pelo menos uma opção.'),
  });

  const initialValues = {
    id: grupoCliente?.id || '',
    nome: grupoCliente?.nome || '',
    clientes: [],
    listaClientes: grupoCliente?.clientes || [],
  };

  const close = () => {
    formik.resetForm({});
    setGrupoCliente({});
    setFormulario('listagem');
    fetchGruposCliente({ q: '', page, size, sort });
  };

  const handleCloseModal = () => {
    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  const handleAdicionar = () => {
    const novoCliente = formik?.values?.clientes || [];

    const clienteExistente = formik?.values?.listaClientes.some((cliente) =>
      novoCliente.some((clienteNovo) => clienteNovo.id === cliente.id)
    );

    if (!clienteExistente) {
      formik.setFieldValue('listaClientes', [...formik?.values?.listaClientes, ...novoCliente]);
    }

    formik.setFieldValue('clientes', []);
  };

  const handleSubmit = async () => {
    if (permissaoUtils.cliente().editar || permissaoUtils.cliente().cadastrar) {
      setLoading(true);
      let _grupoCliente = {
        id: formik?.values?.id || '',
        nome: formik?.values?.nome || '',
        clientesIds: formik?.values?.listaClientes.map((cliente) => cliente.id) || [],
      };

      try {
        if (!_grupoCliente.id && _grupoCliente?.nome?.length >= 3 && _grupoCliente?.clientesIds?.length > 0) {
          await grupoClienteApi().criarGrupoCliente(_grupoCliente);
          showSuccess('Grupo de cliente salvo com sucesso. ');
        } else if (_grupoCliente.id && _grupoCliente?.nome?.length >= 3 && _grupoCliente?.clientesIds?.length > 0) {
          await grupoClienteApi().atualizarGrupoCliente(_grupoCliente);
          showSuccess('Grupo de cliente editado com sucesso. ');
        }
        setFormulario('listagem');
      } catch (err) {
        apiUtils.handleApiError(err);
      } finally {
        setLoading(false);
        formik.resetForm({});
        setGrupoCliente({});
      }
      fetchGruposCliente();
    }
  };

  const removerCliente = (clienteToRemove) => {
    excluirAlert({
      confirmar: async () => {
        try {
          setTimeout(() => {
            let novaListaClientes = formik?.values?.listaClientes.filter(
              (cliente) => cliente.id !== clienteToRemove.id
            );
            formik.setFieldValue('listaClientes', novaListaClientes);
            showSuccess('Cliente excluído com sucesso.');
          }, '1000');
        } catch (err) {
          console.log(err);
        }
      },
    });
  };

  const formik = useFormik({
    initialValues,
    validationSchema: grupoClienteSchema,
    onSubmit: handleSubmit,
  });

  const _errors = {
    nome: formik?.touched?.nome && formik?.errors?.nome,
    listaClientes: formik?.touched?.listaClientes && formik?.errors?.listaClientes,
  };

  return (
    <FormDialogContent
      onSubmit={() => {
        if (formik.values.nome.length >= 3 && formik.values.listaClientes.length > 0) {
          formik.handleSubmit();
        }
      }}
      header={{
        title: 'Grupo de clientes',
        toolbar: (
          <>
            <CancelButton onClick={handleCloseModal}>Cancelar</CancelButton>
            <SaveButton id="add-classe-root" type="submit">
              Salvar
            </SaveButton>
          </>
        ),
      }}
      content={
        <div className="p-16">
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12} md={12} xl={12} lg={12}>
              <TextInput
                id="nome"
                name="nome"
                value={formik?.values?.nome}
                onChange={formik.handleChange}
                error={_errors?.nome}
                touched={!!_errors?.nome}
                helperText={_errors?.nome}
                required
                onBlur={formik.handleBlur}
                label="Nome do grupo"
                variant="outlined"
                maxLength={100}
              />
            </Grid>
            <Grid item xs={8} sm={8} md={8} xl={8} ls={8}>
              <SelectCliente
                id="clientes"
                label="Clientes"
                multiple
                required
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik?.values?.clientes}
                getOptionDisabled={(option) => {
                  let selecionado = false;

                  if (formik.values.clientes.some((value) => value.id === option.id)) {
                    selecionado = true;
                  }

                  return selecionado;
                }}
                error={_errors?.listaClientes}
                touched={!!_errors?.listaClientes}
              />
            </Grid>
            <Grid item xs={4} sm={4} md={4} xl={4} ls={4}>
              <Button size="large" className={classes.btnAdicionar} title="Adicionar" onClick={handleAdicionar}>
                Adicionar
              </Button>
            </Grid>
            <Grid item xs={12} sm={12} md={12} xl={12} lg={12}>
              <CadastroGrupoClienteTable clientes={formik?.values?.listaClientes} onRemove={removerCliente} />
            </Grid>
          </Grid>
        </div>
      }
    />
  );
};

export default CadastroGrupoCliente;
